import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SectionHeading from "@/components/ui/SectionHeading";
import ScriptText from "@/components/ui/ScriptText";

gsap.registerPlugin(ScrollTrigger);

interface TimeBlock {
  time: string;
  description: string;
}

interface SampleItineraryProps {
  scriptAccent?: string;
  heading: string;
  timeBlocks: TimeBlock[];
}

export default function SampleItinerary({
  scriptAccent,
  heading,
  timeBlocks,
}: SampleItineraryProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion) return;

    const rows = el.querySelectorAll(".itinerary-row");
    rows.forEach((row, i) => {
      gsap.fromTo(
        row,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: row,
            start: "top 88%",
            toggleActions: "play none none none",
          },
          delay: i * 0.05,
        },
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach((st) => {
        const trigger = st.trigger;
        if (trigger && el.contains(trigger)) st.kill();
      });
    };
  }, []);

  return (
    <section ref={ref} className="bg-cream px-6 py-28 lg:px-12 lg:py-44">
      <div className="mx-auto max-w-4xl">
        <SectionHeading scriptAccent={scriptAccent} heading={heading} />

        <div className="mx-auto mt-16 max-w-3xl space-y-0 divide-y divide-warm-stone/20">
          {timeBlocks.map((block, i) => (
            <div
              key={i}
              className="itinerary-row grid gap-2 py-8 first:pt-0 last:pb-0 sm:grid-cols-[140px_1fr] sm:gap-8"
            >
              <ScriptText className="text-2xl lg:text-3xl">
                {block.time}
              </ScriptText>
              <p className="font-body text-sm leading-relaxed text-soft-charcoal/70 lg:text-base">
                {block.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
